/**
 * Recurring mission reminders.
 *
 *   every N minutes or hours          (rolling, from the first ping)
 *   every N days at a clock time
 *   every N weeks on a weekday at a clock time
 *   every N months on a day-of-month at a clock time
 *       (months that are shorter use the last day)
 *
 * The first ping is the soonest slot still ahead. Later pings step by N.
 * Ends never, after N occurrences, or at a final instant.
 *
 * `at` is the next occurrence. `recur.anchor` is the first.
 * `recur.fired` is how many occurrences are already used up.
 *
 * Keep infrastructure/lambdas/fokus-clock/recur.mjs in lockstep with this file.
 */

export const RECUR_UNITS = ['minute', 'hour', 'day', 'week', 'month'];
export const RECUR_ENDS = ['never', 'times', 'until'];

const MAX_EVERY = 999;
const MAX_TIMES = 999;
const MAX_STEPS = 100000;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const zoneFormats = new Map();

function zoneFormatter(timeZone) {
  let fmt = zoneFormats.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    zoneFormats.set(timeZone, fmt);
  }
  return fmt;
}

function zoneParts(ms, timeZone) {
  const bag = {};
  for (const part of zoneFormatter(timeZone).formatToParts(new Date(ms))) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  let hour = Number(bag.hour);
  if (hour === 24) hour = 0;
  return {
    year: Number(bag.year),
    month: Number(bag.month),
    day: Number(bag.day),
    hour,
    minute: Number(bag.minute),
    second: Number(bag.second) || 0,
  };
}

function zoneOffsetMs(utcMs, timeZone) {
  const p = zoneParts(utcMs, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - utcMs;
}

function zonedToUtc(year, month, day, hour, minute, timeZone) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offset1 = zoneOffsetMs(utcGuess, timeZone);
  let instant = utcGuess - offset1;
  const offset2 = zoneOffsetMs(instant, timeZone);
  if (offset2 !== offset1) instant = utcGuess - offset2;
  return instant;
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function addDays(date, amount) {
  const utc = new Date(Date.UTC(date.year, date.month - 1, date.day));
  utc.setUTCDate(utc.getUTCDate() + amount);
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

function addMonths(year, month, monthDay, count) {
  const index = (month - 1) + count;
  const yearShift = Math.floor(index / 12);
  const monthIndex = ((index % 12) + 12) % 12;
  const nextYear = year + yearShift;
  const nextMonth = monthIndex + 1;
  return {
    year: nextYear,
    month: nextMonth,
    day: Math.min(monthDay, daysInMonth(nextYear, nextMonth)),
  };
}

function weekdayOf(year, month, day) {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function clampInt(value, min, max) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return null;
  if (n < min || n > max) return null;
  return n;
}

function looseInt(value, min, max, fallback) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function normalizeClock(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function isValidTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

export function localTimeZone() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(zone) ? zone : 'UTC';
  } catch {
    return 'UTC';
  }
}

function parseClock(hhmm) {
  const [hour, minute] = hhmm.split(':').map(Number);
  return [hour, minute];
}

function stepMs(recur) {
  const unit = recur.unit === 'hour' ? 60 * 60 * 1000 : 60 * 1000;
  return recur.every * unit;
}

function calendarStep(recur, fromMs) {
  const parts = zoneParts(fromMs, recur.timeZone);
  const [hour, minute] = parseClock(recur.time);
  let nextDate;
  if (recur.unit === 'month') {
    nextDate = addMonths(parts.year, parts.month, recur.monthDay, recur.every);
  } else {
    const days = recur.unit === 'week' ? recur.every * 7 : recur.every;
    nextDate = addDays(parts, days);
  }
  return zonedToUtc(nextDate.year, nextDate.month, nextDate.day, hour, minute, recur.timeZone);
}

function stepOnce(recur, fromMs) {
  if (recur.unit === 'minute' || recur.unit === 'hour') {
    return fromMs + stepMs(recur);
  }
  let next = calendarStep(recur, fromMs);
  if (Number.isFinite(next) && next <= fromMs) {
    next = calendarStep(recur, fromMs + 60 * 1000);
  }
  return next;
}

function firstFutureSlot(recur, nowMs) {
  if (recur.unit === 'minute' || recur.unit === 'hour') {
    return nowMs + stepMs(recur);
  }
  const parts = zoneParts(nowMs, recur.timeZone);
  const [hour, minute] = parseClock(recur.time);
  if (recur.unit === 'month') {
    let cursor = addMonths(parts.year, parts.month, recur.monthDay, 0);
    let instant = zonedToUtc(cursor.year, cursor.month, cursor.day, hour, minute, recur.timeZone);
    if (instant <= nowMs) {
      cursor = addMonths(parts.year, parts.month, recur.monthDay, 1);
      instant = zonedToUtc(cursor.year, cursor.month, cursor.day, hour, minute, recur.timeZone);
    }
    return instant;
  }

  let cursor = { year: parts.year, month: parts.month, day: parts.day };
  if (recur.unit === 'week') {
    const delta = (recur.weekday - weekdayOf(cursor.year, cursor.month, cursor.day) + 7) % 7;
    cursor = addDays(cursor, delta);
  }
  let instant = zonedToUtc(cursor.year, cursor.month, cursor.day, hour, minute, recur.timeZone);
  if (instant <= nowMs) {
    cursor = addDays(cursor, recur.unit === 'week' ? 7 : 1);
    instant = zonedToUtc(cursor.year, cursor.month, cursor.day, hour, minute, recur.timeZone);
  }
  return instant;
}

/**
 * Stored shape, or null when the rule can't fire.
 * `fired` and `anchor` are kept when present; scheduling a new series sets those itself.
 */
export function normalizeRecur(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (!RECUR_UNITS.includes(raw.unit)) return null;
  const every = clampInt(raw.every, 1, MAX_EVERY);
  if (!every) return null;
  const end = RECUR_ENDS.includes(raw.end) ? raw.end : null;
  if (!end) return null;

  const needsClock = raw.unit === 'day' || raw.unit === 'week' || raw.unit === 'month';
  const time = needsClock ? normalizeClock(raw.time) : undefined;
  if (needsClock && !time) return null;
  const weekday = raw.unit === 'week' ? clampInt(raw.weekday, 0, 6) : undefined;
  if (raw.unit === 'week' && weekday == null) return null;
  const monthDay = raw.unit === 'month' ? clampInt(raw.monthDay, 1, 31) : undefined;
  if (raw.unit === 'month' && monthDay == null) return null;

  let times;
  let until;
  if (end === 'times') {
    times = clampInt(raw.times, 1, MAX_TIMES);
    if (!times) return null;
  }
  if (end === 'until') {
    const untilMs = Date.parse(raw.until);
    if (!Number.isFinite(untilMs)) return null;
    until = new Date(untilMs).toISOString();
  }

  const anchorMs = Date.parse(raw.anchor);
  const recur = {
    every,
    unit: raw.unit,
    end,
    timeZone: isValidTimeZone(raw.timeZone) ? raw.timeZone : 'UTC',
    fired: looseInt(raw.fired, 0, MAX_STEPS, 0),
  };
  if (time) recur.time = time;
  if (weekday != null) recur.weekday = weekday;
  if (monthDay) recur.monthDay = monthDay;
  if (times) recur.times = times;
  if (until) recur.until = until;
  if (Number.isFinite(anchorMs)) recur.anchor = new Date(anchorMs).toISOString();
  return recur;
}

/** First future occurrence for a new series. Null when the end is already past it. */
export function scheduleRecurring(input, nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const recur = normalizeRecur({ ...input, fired: 0, anchor: undefined });
  if (!recur) return null;
  delete recur.anchor;
  const first = firstFutureSlot(recur, now);
  if (!Number.isFinite(first)) return null;
  if (recur.end === 'until' && first > Date.parse(recur.until)) return null;
  const at = new Date(first).toISOString();
  return { at, recur: { ...recur, anchor: at, fired: 0 } };
}

/**
 * Consume every occurrence that is already due.
 * One notification covers the backlog; the count still advances through each slot.
 * @returns {{ reminder: object|null, occurrenceAt: string|null, notify: boolean }}
 */
export function catchUpRecurring(reminder, nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const recur = normalizeRecur(reminder?.recur);
  const due = Date.parse(reminder?.at);
  if (!recur || !Number.isFinite(due)) {
    return { reminder: null, occurrenceAt: null, notify: false };
  }
  if (due > now) {
    return {
      reminder: { ...reminder, recur },
      occurrenceAt: null,
      notify: false,
    };
  }

  const untilMs = recur.end === 'until' ? Date.parse(recur.until) : Infinity;
  let fired = recur.fired || 0;
  let cursor = due;
  const occurrenceAt = new Date(due).toISOString();
  let notify = false;

  for (let guard = 0; guard < MAX_STEPS; guard += 1) {
    if (cursor > now) break;
    if (recur.end === 'until' && cursor > untilMs) {
      return { reminder: null, occurrenceAt: notify ? occurrenceAt : null, notify };
    }
    notify = true;
    fired += 1;
    if (recur.end === 'times' && fired >= recur.times) {
      return { reminder: null, occurrenceAt, notify: true };
    }
    const next = stepOnce(recur, cursor);
    if (!Number.isFinite(next) || next <= cursor) {
      return { reminder: null, occurrenceAt, notify: true };
    }
    if (recur.end === 'until' && next > untilMs) {
      return { reminder: null, occurrenceAt, notify: true };
    }
    cursor = next;
  }

  if (cursor <= now) {
    return { reminder: null, occurrenceAt, notify: true };
  }
  return {
    notify,
    occurrenceAt,
    reminder: {
      ...reminder,
      at: new Date(cursor).toISOString(),
      recur: { ...recur, fired },
    },
  };
}

/** Next ticket time. `done` means the series should leave the clock. */
export function projectRecurringFire(reminder, nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const recur = normalizeRecur(reminder?.recur);
  const atMs = Date.parse(reminder?.at);
  if (!recur || !Number.isFinite(atMs)) return { done: true, fireAt: null, recur: null };
  if (atMs > now) {
    return { done: false, fireAt: new Date(atMs).toISOString(), recur };
  }
  const result = catchUpRecurring(reminder, now);
  if (!result.reminder) return { done: true, fireAt: null, recur: null };
  return { done: false, fireAt: result.reminder.at, recur: result.reminder.recur };
}

const RULE_KEYS = ['every', 'unit', 'time', 'weekday', 'monthDay', 'end', 'times', 'until', 'timeZone', 'anchor'];
const PATTERN_KEYS = ['every', 'unit', 'time', 'weekday', 'monthDay', 'timeZone'];

function sameKeys(left, right, keys) {
  if (!left || !right) return false;
  return keys.every((key) => (left[key] ?? null) === (right[key] ?? null));
}

export function sameRecurRule(a, b) {
  return sameKeys(normalizeRecur(a), normalizeRecur(b), RULE_KEYS);
}

/** Clock pattern only — end conditions can change without restarting the series. */
export function sameRecurPattern(a, b) {
  return sameKeys(normalizeRecur(a), normalizeRecur(b), PATTERN_KEYS);
}

export function formatClockTime(hhmm) {
  const clock = normalizeClock(hhmm);
  if (!clock) return '';
  let hour = Number(clock.slice(0, 2));
  const minute = clock.slice(3);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${suffix}`;
}

export function ordinalDay(day) {
  const n = Number(day);
  const mod = n % 100;
  if (mod >= 11 && mod <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

export function formatRecurringLabel(recur) {
  const rule = normalizeRecur(recur);
  if (!rule) return 'Recurring';
  const n = rule.every;
  if (rule.unit === 'minute') return n === 1 ? 'Every minute' : `Every ${n} minutes`;
  if (rule.unit === 'hour') return n === 1 ? 'Every hour' : `Every ${n} hours`;
  const time = formatClockTime(rule.time);
  if (rule.unit === 'day') return n === 1 ? `Daily at ${time}` : `Every ${n} days at ${time}`;
  if (rule.unit === 'week') {
    const day = WEEKDAYS[rule.weekday] || 'Week';
    return n === 1 ? `${day}s at ${time}` : `Every ${n} weeks on ${day} at ${time}`;
  }
  const ord = ordinalDay(rule.monthDay);
  return n === 1 ? `Monthly on the ${ord} at ${time}` : `Every ${n} months on the ${ord} at ${time}`;
}

export function formatRecurringEnd(recur) {
  const rule = normalizeRecur(recur);
  if (!rule) return '';
  if (rule.end === 'times') {
    const fired = rule.fired || 0;
    const left = Math.max(0, rule.times - fired);
    if (fired === 0) return rule.times === 1 ? 'once' : `${rule.times} times`;
    return left === 1 ? '1 time left' : `${left} times left`;
  }
  if (rule.end === 'until' && rule.until) {
    const date = new Date(rule.until);
    if (Number.isNaN(date.getTime())) return 'until a date';
    const when = date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    return `until ${when}`;
  }
  return 'until removed';
}

export function recurUnitLabel(unit, every) {
  const n = Number(every) === 1 ? 1 : 2;
  const names = {
    minute: ['minute', 'minutes'],
    hour: ['hour', 'hours'],
    day: ['day', 'days'],
    week: ['week', 'weeks'],
    month: ['month', 'months'],
  };
  const pair = names[unit] || [unit, unit];
  return n === 1 ? pair[0] : pair[1];
}
